"use client";
import { IResource } from "@gettactic/api";
import React from "react";
import { useMap } from "@/features/map/MapProvider";
import { mergeRefs, useBooleanState } from "@gettactic/helpers/src";
import { useInitialMapTransform } from "@/features/map/components/MapView/useInitialMapTransform";
import { FloorMap } from "@/features/map/components/MapView/FloorItem/FloorMap";

export interface MapStackSwitcherItemProps {
  floor: IResource;
  floorContainerRef: React.RefObject<HTMLDivElement | null>;
  mapContainerRef: React.RefObject<HTMLDivElement | null>;
  index: number;
  onItemLoaded?: (
    svg: SVGSVGElement | undefined | null,
    floor: IResource
  ) => void;
  isAllItemsLoaded?: boolean;
  floorItemsCount: number;
  onMouseEnter?: (index: number) => void;
  onMouseLeave?: (index: number) => void;
  onClick?: (index: number) => void;
  hoveringIndex: number | null;
  selectedIndex: number | null;
  isFlattening: boolean;
}

export const MapStackSwitcherItem: React.FC<MapStackSwitcherItemProps> = ({
  floor,
  floorContainerRef,
  index,
  mapContainerRef,
  onItemLoaded,
  isAllItemsLoaded,
  floorItemsCount,
  onMouseEnter,
  onMouseLeave,
  onClick,
  hoveringIndex,
  selectedIndex,
  isFlattening
}) => {
  const { isMapEditing } = useMap();
  const svgRef = React.useRef<SVGSVGElement | null>(null);
  const isSvgLoaded = useBooleanState();
  const self = React.useRef<HTMLDivElement | null>(null);

  const mapTransform = useInitialMapTransform({
    itemWrapperRef: self,
    isSvgLoaded: isSvgLoaded.state,
    floorContainerRef,
    mapContainerRef
  });

  const isHovering = hoveringIndex === index;

  let extraZ = 0;
  let extraY = -(floorItemsCount * 120);
  let opacity = 1;
  const itemsDistanceDelta = hoveringIndex === null ? 100 : 200;

  if (selectedIndex !== null) {
    if (index < selectedIndex) {
      const distance = selectedIndex - index * 4;
      extraZ = 200 * distance;
      extraY += -120 * distance;
      opacity = 0;
    } else if (index > selectedIndex) {
      const distance = index - selectedIndex;
      extraZ = -200 * distance;
      extraY += 120 * distance;
      opacity = 0;
    }
  } else if (hoveringIndex !== null) {
    if (index < hoveringIndex) {
      const distance = hoveringIndex - index;
      extraZ = 100 * distance;
      extraY += -20 * distance;
      opacity = 0.3;
    } else if (index > hoveringIndex) {
      const distance = index - hoveringIndex;
      extraZ = -100 * distance;
      extraY += 20 * distance;
      opacity = 0.3;
    }
  }

  const svgStyle = {
    display: "block",
    borderRadius: 8,
    cursor: "default"
  };

  const itemStyle: React.HTMLAttributes<HTMLDivElement>["style"] = {
    transformOrigin: "top left",
    perspectiveOrigin: "center center",
    position: "absolute",
    left: 0,
    boxSizing: "border-box",
    transformStyle: "preserve-3d",
    transform: `rotateX(0deg) translate(${String(mapTransform?.translateX || 0)}px, ${String(mapTransform?.translateY || 0)}px) scale(${mapTransform?.scale || 1}) translateZ(0px)`,
    //transition: "transform 0.4s ease, opacity 0.4s ease",
    pointerEvents: "auto",
    padding: 0,
    opacity: 1,
    cursor: "pointer",
    outline:
      isHovering && !isFlattening
        ? "10px solid rgba(37,99,235, .4)"
        : "10px solid transparent",
    boxShadow:
      isHovering && !isFlattening
        ? "0 24px 46px 10px rgba(0, 0, 0, 0.10)"
        : undefined,
    borderRadius: isHovering ? 10 : 8
  };

  const mapContainerStyle: React.HTMLAttributes<HTMLDivElement>["style"] = {
    pointerEvents: "none",
    transitionDuration: "0.6s",
    transform:
      isAllItemsLoaded && !isFlattening
        ? `translateZ(-${index * itemsDistanceDelta}px) translateZ(${extraZ}px) translateY(${extraY}px)`
        : undefined,
    opacity: opacity
  };

  const loadSvgAsImage = true;

  return (
    <div
      style={mapContainerStyle}
      className={"item-main-container transition-all"}
    >
      <div
        className={"item-wrapper"}
        ref={mergeRefs([self])}
        key={floor.id}
        style={itemStyle}
        onMouseEnter={() => onMouseEnter?.(index)}
        onMouseLeave={() => onMouseLeave?.(index)}
        onClick={() => onClick?.(index)}
      >
        <FloorMap
          isEditing={isMapEditing}
          style={svgStyle}
          floor={floor}
          onMapLoaded={(_, svg) => {
            if (loadSvgAsImage || svg) {
              svgRef.current = svg || null;
              isSvgLoaded.true();
            }

            setTimeout(() => {
              onItemLoaded?.(svg, floor);
            }, 500);
          }}
          //onMapEditLoaded={(error, svg) => onMapEditLoaded(error, svg)}
          label={floor.name ?? `Floor ${index + 1}`}
          showLabel
          loadSvgAsImage={loadSvgAsImage}
          //renderAsImage
        />
      </div>
    </div>
  );
};
